import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu, TextInput } from '@mantine/core';
import { useMemo, useState } from 'react';
import { groupHistoriesByDate } from '../../../../components/chat-history-panel/chat-history-panel.helpers';
import { Text } from '../../../../ui-library/typography/typography';
import { FloraSpinner } from '../flora-spinner';
import type { ChatHistory } from '../../flora.type';

/** Longest chat name a rename may save; longer input is truncated. */
const MAX_CHAT_NAME_LENGTH = 50;

interface FloraHistoryListProps {
  chatHistories: ChatHistory[];
  activeChatId: string | undefined;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string, newDescription: string) => void;
  /**
   * Marks the active row with a check that yields to the ⋯ button on hover or
   * while its menu is open. Off for the full-page sidebar, which signals the
   * active chat by background alone.
   */
  showActiveCheck?: boolean;
  /**
   * Portals each row's ⋯ menu to the body (above the Flora aside) so it isn't
   * clipped by a short, overflow-hidden host such as the side panel dropdown.
   */
  menuWithinPortal?: boolean;
  /** Histories haven't arrived yet: show a spinner rather than the empty label. */
  isLoading?: boolean;
  emptyLabel?: string;
}

/**
 * Renders chat histories grouped by date, with a hover ⋯ menu to rename
 * (inline) or delete each chat. Shared by the full-page Flora sidebar and the
 * side panel's history dropdown so both use one row style.
 *
 * Renders a fragment — the caller owns the scroll container and its padding.
 *
 * @param {ChatHistory[]} chatHistories - Histories to list; grouped and sorted newest first
 * @param {string | undefined} activeChatId - Id of the open chat, highlighted in the list
 * @param {(id: string) => void} onSelect - Opens a chat; not fired while that row is being renamed
 * @param {(id: string) => void} onDelete - Deletes a chat
 * @param {(id: string, newDescription: string) => void} onEdit - Saves a non-empty, trimmed rename
 * @param {boolean} [showActiveCheck] - Shows a check on the active row
 * @param {boolean} [menuWithinPortal] - Portals the row menus so a clipping host can't cut them off
 * @param {boolean} [isLoading] - Shows a spinner in place of an empty list while histories load
 * @param {string} [emptyLabel] - Text shown when there are no histories
 * @returns {JSX.Element} The grouped history rows
 */
function FloraHistoryList({
  chatHistories,
  activeChatId,
  onSelect,
  onDelete,
  onEdit,
  showActiveCheck = false,
  menuWithinPortal = false,
  isLoading = false,
  emptyLabel = 'No conversations yet',
}: FloraHistoryListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  // The name the rename started from: saving it untouched is a no-op, so an
  // existing name longer than the limit isn't cut just by opening Rename.
  const [originalValue, setOriginalValue] = useState('');

  const groups = useMemo(() => groupHistoriesByDate(chatHistories), [chatHistories]);

  const startEdit = (id: string, currentDesc: string) => {
    setEditingId(id);
    setEditValue(currentDesc);
    setOriginalValue(currentDesc);
  };

  const saveEdit = () => {
    const trimmed = editValue.trim().slice(0, MAX_CHAT_NAME_LENGTH);
    if (editingId && trimmed && editValue !== originalValue) {
      onEdit(editingId, trimmed);
    }
    setEditingId(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  return (
    <>
      {groups.length === 0 && isLoading && (
        <LoadingState role="status" aria-label="Loading chats">
          <FloraSpinner />
        </LoadingState>
      )}
      {groups.length === 0 && !isLoading && (
        <EmptyText size="small" color="var(--ink-lighter)">
          {emptyLabel}
        </EmptyText>
      )}
      {groups.map((group) => (
        <DateGroup key={group.label}>
          <DateHeaderText size="tiny" weight="bold" color="var(--ink-lighter)">
            {group.label}
          </DateHeaderText>
          {group.histories.map((h) => {
            const isActive = h.id === activeChatId;
            return (
              <HistoryRow
                key={h.id}
                $active={isActive}
                data-show-check={showActiveCheck && isActive ? 'true' : undefined}
                onClick={() => {
                  if (editingId !== h.id) onSelect(h.id);
                }}
              >
                {editingId === h.id ? (
                  <RenameInput
                    value={editValue}
                    onChange={(e) => setEditValue(e.currentTarget.value.slice(0, MAX_CHAT_NAME_LENGTH))}
                    maxLength={MAX_CHAT_NAME_LENGTH}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit();
                      if (e.key === 'Escape') {
                        // Keep Escape from also closing a surrounding dropdown.
                        e.stopPropagation();
                        cancelEdit();
                      }
                    }}
                    onBlur={saveEdit}
                    autoFocus
                    size="xs"
                    variant="unstyled"
                    aria-label="Chat name"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <>
                    {/* A real button so keyboard users can open the chat; its
                        click bubbles to the row's handler. */}
                    <RowSelectButton type="button" aria-current={isActive ? 'true' : undefined}>
                      <RowTextStyled
                        size="small"
                        color={isActive && showActiveCheck ? 'var(--ink-darkest)' : 'var(--ink-dark)'}
                        weight={isActive && showActiveCheck ? 'medium' : undefined}
                        title={h.description}
                      >
                        {h.description || 'Untitled'}
                      </RowTextStyled>
                    </RowSelectButton>
                    {showActiveCheck && isActive && (
                      <CheckIcon className="row-check" data-testid="active-chat-check" aria-hidden="true">
                        <IconifyIcon icon="material-symbols:check" width={16} height={16} />
                      </CheckIcon>
                    )}
                    <Menu
                      shadow="sm"
                      radius="md"
                      position="bottom-end"
                      offset={4}
                      withinPortal={menuWithinPortal}
                      zIndex={menuWithinPortal ? 600 : undefined}
                    >
                      <Menu.Target>
                        <MoreButton
                          className="row-actions"
                          onClick={(e) => e.stopPropagation()}
                          aria-label="More options"
                        >
                          <IconifyIcon icon="material-symbols:more-horiz" width={16} height={16} />
                        </MoreButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <RowMenuItem
                          leftSection={<IconifyIcon icon="material-symbols:edit-outline" width={14} height={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            startEdit(h.id, h.description);
                          }}
                        >
                          Rename
                        </RowMenuItem>
                        <RowMenuItem
                          leftSection={<IconifyIcon icon="material-symbols:delete-outline" width={14} height={14} />}
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(h.id);
                          }}
                        >
                          Delete
                        </RowMenuItem>
                      </Menu.Dropdown>
                    </Menu>
                  </>
                )}
              </HistoryRow>
            );
          })}
        </DateGroup>
      ))}
    </>
  );
}

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const EmptyText = styled(Text)`
  text-align: center;
  margin-top: 40px;
`;

/* Borderless inline rename field sized to sit in place of the row label. */
const RenameInput = styled(TextInput)`
  flex: 1;
  min-width: 0;

  .mantine-TextInput-input {
    font-size: var(--font-size-small);
    padding: 0;
    height: auto;
    min-height: unset;
    color: var(--ink-darkest);
  }
`;

/* 13px has no design token; it matches the compact row menu used across Flora. */
const RowMenuItem = styled(Menu.Item)`
  font-size: 13px;
`;

const DateGroup = styled.div`
  margin-bottom: 4px;
`;

const DateHeaderText = styled(Text)`
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 14px 8px 6px;
  user-select: none;
`;

const HistoryRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
  background: ${({ $active }) => ($active ? 'var(--sky-lighter)' : 'transparent')};
  gap: 4px;

  &:hover {
    background: var(--sky-lighter);

    .row-actions {
      opacity: 1;
    }
  }

  /* Keyboard focus anywhere in the row reveals its ⋯ button. */
  &:focus-within .row-actions {
    opacity: 1;
  }

  /* Active row with a check: the check holds the ⋯ slot until the row is
     hovered, focused, or its menu is open. */
  &[data-show-check='true'] .row-actions:not([aria-expanded='true']) {
    display: none;
  }

  &[data-show-check='true']:hover .row-actions,
  &[data-show-check='true']:focus-within .row-actions,
  &[data-show-check='true'] .row-actions[aria-expanded='true'] {
    display: flex;
    opacity: 1;
  }

  &[data-show-check='true']:hover .row-check,
  &[data-show-check='true']:focus-within .row-check {
    display: none;
  }

  /* Kept separate: a selector list containing an unsupported :has() would
     drop the whole rule, hover included. */
  &[data-show-check='true']:has(.row-actions[aria-expanded='true']) .row-check {
    display: none;
  }
`;

const RowSelectButton = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  text-align: left;
  color: inherit;
  cursor: pointer;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const RowTextStyled = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: var(--secondary-base);
`;

const MoreButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--ink-lighter);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s, background 0.12s, color 0.12s;
  flex-shrink: 0;

  &:hover {
    background: var(--flora-hover-strong);
    color: var(--ink-dark);
  }

  &:focus-visible {
    opacity: 1;
    outline: 2px solid var(--secondary-base);
    outline-offset: 1px;
  }
`;

export { FloraHistoryList, MAX_CHAT_NAME_LENGTH };
